import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip } from './ui/Tooltip';
import { RadiantStar } from './ui/RadiantStar';
export function AboutSection() {
  const [isExpanded, setIsExpanded] = useState(false);
  return <section id="about" className="relative py-32 overflow-hidden" data-id="element-1">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-2">
        <img src="https://images.unsplash.com/photo-1516653980844-c68233954853?q=80&w=2070&auto=format&fit=crop" alt="" className="w-full h-full object-cover object-bottom" data-id="element-3" />
        <div className="absolute inset-0 bg-gradient-to-r from-night-900 via-night-900/80 to-night-900/40" data-id="element-4"></div>
        <div className="absolute inset-0 bg-grain opacity-20 mix-blend-overlay" data-id="element-5"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-6">
        <div className="grid md:grid-cols-2 gap-12 items-start" data-id="element-7">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-8">
            <div className="flex items-center gap-4 mb-8" data-id="element-9">
              <h2 className="font-serif text-4xl md:text-5xl text-gold-400 text-glow" data-id="element-10">
                About Me: Grounded in Connection
              </h2>
              <div className="hidden sm:block" data-id="element-11">
                <RadiantStar size={40} delay={0.3} data-id="element-12" />
              </div>
            </div>

            <div className="space-y-6 text-cream-50/90 text-lg leading-relaxed" data-id="element-13">
              {/* Teaser Text - Always Visible */}
              <p data-id="element-14">
                I've always been an engineer at heart — curious, analytical, and
                driven to understand how things work. But over the years, I
                discovered something even more meaningful to me than systems and
                structures: people.
              </p>
              <p data-id="element-15">
                My journey through business and leadership taught me one thing:
                I fell in love with helping people grow. Today, I combine
                everything I've learned to help you reconnect with yourself and
                make confident decisions about your next adventure in life.
              </p>

              {/* Expandable Text */}
              <AnimatePresence data-id="element-16">
                {isExpanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className="overflow-hidden space-y-6" data-id="element-17">
                    <p data-id="element-18">
                      My career took me through roles in sales, business
                      development, and eventually into leadership as a manager
                      of a supply chain team. And while I enjoyed the challenges
                      of each step, something unexpected happened along the way.
                    </p>
                    <p data-id="element-19">
                      Whether it was guiding ambitious team members, supporting
                      colleagues through tough decisions, or helping someone
                      rediscover their confidence, I found myself consistently
                      drawn to the coaching side of every role I had.
                    </p>
                    <p data-id="element-20">
                      Even when my work became more strategic and my team more
                      senior, I noticed something was missing. That's when I
                      chose to return to what energizes me most.
                    </p>
                    <p data-id="element-21">
                      I began my formal journey with the International Coaching
                      Federation (ICF) to refine my skills, deepen my practice,
                      and build a foundation rooted in ethics, trust, and
                      professionalism. My engineering clarity, business
                      experience, leadership insight, and a deep appreciation
                      for nature all come together in how I coach today.
                    </p>
                  </motion.div>}
              </AnimatePresence>

              {/* Read More / Read Less Toggle */}
              <button onClick={() => setIsExpanded(!isExpanded)} className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 font-medium transition-colors focus:outline-none group" data-id="element-22">
                <span data-id="element-23">{isExpanded ? 'Read less' : 'Read my full story'}</span>
                {isExpanded ? <ChevronUp size={20} className="group-hover:-translate-y-1 transition-transform" data-id="element-24" /> : <ChevronDown size={20} className="group-hover:translate-y-1 transition-transform" data-id="element-25" />}
              </button>
            </div>

            {/* ICF Badge - Always Visible */}
            <div className="mt-8 space-y-3" data-id="element-26">
              <div className="flex items-center space-x-3 text-gold-400/80 bg-night-900/50 backdrop-blur-sm p-4 rounded-lg inline-flex border border-gold-400/20" data-id="element-27">
                <CheckCircle2 size={20} data-id="element-28" />
                <span className="font-medium tracking-wide text-sm" data-id="element-29">
                  ICF Member (ACC Credential Pending)
                </span>
              </div>
              <p className="text-cream-50/70 text-sm leading-relaxed max-w-lg" data-id="element-30">
                I believe in and apply the{' '}
                <a href="https://coachingfederation.org/resource/icf-core-competencies/" target="_blank" rel="noopener noreferrer" className="text-gold-400 hover:text-gold-300 underline underline-offset-2 transition-colors" data-id="element-31">
                  ICF Core Competencies
                </a>{' '}
                in my coaching practice.
              </p>
            </div>
          </motion.div>

          {/* Right side - Mark's portrait */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="hidden md:flex items-start justify-center" data-id="element-32">
            <div className="relative" data-id="element-33">
              <div className="absolute -inset-3 bg-gold-400/10 rounded-2xl transform rotate-2" data-id="element-34"></div>
              <img src="https://cdn.magicpatterns.com/uploads/kDhaJTXSWSxVvnG8YrKPtw/WhatsApp_Image_2026-03-07_at_11.07.46_magicstudio_os8ia0nr4xa.png" alt="Mark Vermeulen" className="relative rounded-2xl shadow-2xl w-full max-w-[10rem] object-cover object-center aspect-[3/4] border border-gold-400/20" data-id="element-35" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}